import {
  Button,
  TextField,
  Box,
  Grid,
  Paper,
  Autocomplete,
  Stack,
  Switch,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from '@mui/material';
import { Form, Formik } from 'formik';
import useImageSelect from '../../hooks/useImageSelect';
import { useEffect, useState } from 'react';
import IconPicker from '../../components/IconPicker';
import IconPickerToggle from '../../components/IconPickerToggle';
import CreateEntityDecorator from '../../components/CreateEntityDecorator';
import TagCreate from '../tags/TagCreate';
import useCheckPermission from '../../hooks/useCheckPermission';
import ColorPickerField from '../../components/ColorPickerField';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

const MobileApplicationThemeForm = (props: any) => {
  const {
    entity,
    onSubmit,
    errors,
    handleCancel,
    publishers,
    readonly = false,
    existingThemes,
  } = props;

  const { checkPermission } = useCheckPermission();

  const { ImageSelectComponent } = useImageSelect();
  const second = useImageSelect();
  const { enqueueSnackbar } = useSnackbar();

  const [publisherId, setPublisherId] = useState<number | null>(
    entity.logo?.publisherId ?? entity.backgroundImage?.publisherId ?? null
  );

  useEffect(() => {
    const fromEntity =
      entity.logo?.publisherId ?? entity.backgroundImage?.publisherId ?? null;
    if (fromEntity) {
      setPublisherId(fromEntity);
    }
  }, [entity?.id, entity?.logo?.publisherId, entity?.backgroundImage?.publisherId]);

  const imageId = (image: any) =>
    Number(image?.id ?? image?.Id ?? 0) || 0;

  const isDateInvalid = (date: any, id: any) => {
    let dateFormat = new Date(date);
    return (existingThemes ?? []).some((x: any) => {
      if (x.id === id) return false;
      let startDate = new Date(x.startDate);
      let endDate = new Date(x.endDate);
      let result = dateFormat >= startDate && dateFormat <= endDate;

      return result;
    });
  };
  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );

  return (
    <Formik
      initialValues={entity}
      // Only reinit on edit load — create parent re-renders must not wipe logoId.
      enableReinitialize={!!entity?.id && entity.id > 0}
      validate={(values) => {
        const errors: any = {};
        const logoId = values.logoId || imageId(values.logo);
        const backgroundImageId =
          values.backgroundImageId || imageId(values.backgroundImage);
        if (!logoId) {
          errors.logoId = 'Logó kötelező';
        }
        if (!backgroundImageId) {
          errors.backgroundImageId = 'Háttérkép kötelező';
        }
        if (!values.startDate) {
          errors.startDate = 'Kezdő dátum kötelező';
        }
        if (!values.endDate) {
          errors.endDate = 'Vég dátum kötelező';
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        const logoId = values.logoId || imageId(values.logo);
        const backgroundImageId =
          values.backgroundImageId || imageId(values.backgroundImage);
        onSubmit(
          {
            ...values,
            logoId,
            backgroundImageId,
          },
          setSubmitting
        );
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        setFieldValue,
        handleChange,
        errors: validationErrors,
      }) => (
        <Form>
          <Paper>
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "left",
                p: 5
              }}>
              <h2>
                Applikáció téma {values.id > 0 ? 'szerkesztése' : 'létrehozása'}{' '}
              </h2>
              <Grid size={12} sx={{
                pt: 5
              }}>
                <Box sx={{
                  color: "red"
                }}>
                  {errors.map((error: string) => (
                    <li>{error}</li>
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  disablePortal
                  id="publisherId"
                  disabled={readonly}
                  value={publisherId}
                  onChange={(event, value) => {
                    setPublisherId(value);
                  }}
                  getOptionLabel={(option) =>
                    publishers?.find((g: any) => g.id === option)?.name ?? ''
                  }
                  options={publishers?.map((g: any) => g.id) ?? []}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      fullWidth
                      label="Galéria választás"
                      helperText={
                        !publisherId
                          ? 'Előbb válassz galériát, utána tölts fel / válassz képet.'
                          : undefined
                      }
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="h5">Logó:</Typography>
                {validationErrors.logoId && (
                  <Typography color="error" variant="body2">
                    {String(validationErrors.logoId)}
                  </Typography>
                )}
              </Grid>
              <Grid size={12}>
                <ImageSelectComponent
                  image={values.logo}
                  onSelect={(image: any) => {
                    const id = imageId(image);
                    setFieldValue('logoId', id);
                    setFieldValue('logo', image);
                  }}
                  alt="kép"
                  onUploaded={(image: any) => {
                    const id = imageId(image);
                    setFieldValue('logoId', id);
                    setFieldValue('logo', image);
                  }}
                  publisherId={publisherId}
                  currentFolderId={'-1'}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="h5">Háttérkép:</Typography>
                {validationErrors.backgroundImageId && (
                  <Typography color="error" variant="body2">
                    {String(validationErrors.backgroundImageId)}
                  </Typography>
                )}
              </Grid>
              <Grid size={12}>
                <second.ImageSelectComponent
                  image={values.backgroundImage}
                  onSelect={(image: any) => {
                    const id = imageId(image);
                    setFieldValue('backgroundImageId', id);
                    setFieldValue('backgroundImage', image);
                  }}
                  alt="kép"
                  onUploaded={(image: any) => {
                    const id = imageId(image);
                    setFieldValue('backgroundImageId', id);
                    setFieldValue('backgroundImage', image);
                  }}
                  publisherId={publisherId}
                  currentFolderId={'-1'}
                />
              </Grid>
              <Grid size={12}>
                <ColorPickerField
                  label="Elsődleges szín"
                  value={values.primaryColor}
                  readonly={readonly}
                  onChange={(hex) => setFieldValue('primaryColor', hex)}
                />
              </Grid>
              <Grid size={12}>
                <ColorPickerField
                  label="Másodlagos szín"
                  value={values.secondaryColor}
                  readonly={readonly}
                  onChange={(hex) => setFieldValue('secondaryColor', hex)}
                />
              </Grid>
              <Grid size={12}>
                <ColorPickerField
                  label="Elsődleges ikon szín"
                  value={values.iconPrimaryColor}
                  readonly={readonly}
                  onChange={(hex) => setFieldValue('iconPrimaryColor', hex)}
                />
              </Grid>
              <Grid size={12}>
                <ColorPickerField
                  label="Másodlagos ikon szín"
                  value={values.iconSecondaryColor}
                  readonly={readonly}
                  onChange={(hex) => setFieldValue('iconSecondaryColor', hex)}
                />
              </Grid>
              <Grid size={12}>
                <ColorPickerField
                  label="Elsődleges illusztráció szín"
                  value={values.illustrationPrimaryColor}
                  readonly={readonly}
                  onChange={(hex) =>
                    setFieldValue('illustrationPrimaryColor', hex)
                  }
                />
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label="Kezdő dátum"
                  sx={{ width: '100%' }}
                  value={values?.startDate ? dayjs(values.startDate) : null}
                  disabled={readonly}
                  shouldDisableDate={(date) => {
                    return isDateInvalid(date, values.id);
                  }}
                  onChange={(value) => {
                    if (!isDateInvalid(value, values.id)) {
                      setFieldValue('startDate', value);
                    } else {
                      enqueueSnackbar(
                        'Erre az időszakra már létezik egy téma! Kérem válasszon másik időszakot!',
                        { variant: 'error' }
                      );
                    }
                  }}
                  format="YYYY-MM-DD"
                />
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label="Vég dátum"
                  sx={{ width: '100%' }}
                  value={values?.endDate ? dayjs(values.endDate) : null}
                  disabled={readonly}
                  shouldDisableDate={(date) => {
                    return isDateInvalid(date, values.id);
                  }}
                  onChange={(value) => {
                    if (!isDateInvalid(value, values.id)) {
                      setFieldValue('endDate', value);
                    } else {
                      enqueueSnackbar(
                        'Erre az időszakra már létezik egy téma! Kérem válasszon másik időszakot!',
                        { variant: 'error' }
                      );
                    }
                  }}
                  format="YYYY-MM-DD"
                />
              </Grid>
              <Grid
                container
                sx={{
                  pt: 3,
                  justifyContent: "left"
                }}>
                {!readonly && (
                  <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{
                    p: 1
                  }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{
                  p: 1
                }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth={isSmallScreen}
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  );
};

export default MobileApplicationThemeForm;
